import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { masterFirebaseService } from '../services/firebaseService';
import { Empresa, Assinatura } from '../types';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  CreditCard,
  Users,
  GraduationCap,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ToggleLeft,
  ToggleRight,
  FileText,
  Clock,
} from 'lucide-react';

const ClienteDetalhes: React.FC = () => {
  const { cnpj } = useParams<{ cnpj: string }>();
  const navigate = useNavigate();

  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [assinaturas, setAssinaturas] = useState<Assinatura[]>([]);
  const [contagens, setContagens] = useState<{ alunos: number; alunosAtivos: number; usuarios: number }>({ alunos: 0, alunosAtivos: 0, usuarios: 0 });
  const [mensalidades, setMensalidades] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [alterandoStatus, setAlterandoStatus] = useState(false);

  useEffect(() => {
    if (!cnpj) return;
    loadDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpj]);

  const loadDados = async () => {
    if (!cnpj) return;
    setLoading(true);
    try {
      const [emp, ass, cont, mens] = await Promise.all([
        masterFirebaseService.getEmpresaDetalhes(cnpj),
        masterFirebaseService.getAssinaturasEmpresa(cnpj),
        masterFirebaseService.getContagens(cnpj),
        masterFirebaseService.getMensalidadesEmpresa(cnpj),
      ]);
      setEmpresa(emp);
      setAssinaturas(ass);
      setContagens(cont);
      setMensalidades(mens);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAlterarStatus = async () => {
    if (!empresa || !cnpj) return;
    setAlterandoStatus(true);
    try {
      const novoStatus = !empresa.status;
      await masterFirebaseService.alterarStatusEmpresa(cnpj, novoStatus);
      setEmpresa({ ...empresa, status: novoStatus });
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    } finally {
      setAlterandoStatus(false);
    }
  };

  const formatCNPJ = (cnpj: string) => {
    const n = cnpj.replace(/\D/g, '');
    return n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (dateStr.includes('T') && dateStr.endsWith('Z')) {
        return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
      }
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value: number) => {
    return `R$ ${value.toFixed(2).replace('.', ',')}`;
  };

  const getStatusAssinatura = (status: string) => {
    const map: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      ACTIVE: { label: 'Ativa', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3.5 w-3.5" /> },
      OVERDUE: { label: 'Vencida', color: 'bg-red-100 text-red-700', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
      INACTIVE: { label: 'Inativa', color: 'bg-gray-100 text-gray-600', icon: <XCircle className="h-3.5 w-3.5" /> },
      EXPIRED: { label: 'Expirada', color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="h-3.5 w-3.5" /> },
    };
    return map[status] || { label: status, color: 'bg-gray-100 text-gray-600', icon: <FileText className="h-3.5 w-3.5" /> };
  };

  const mensalidadesVencidas = mensalidades.filter(m => {
    const s = (m.status || '').toUpperCase();
    if (s === 'OVERDUE' || s === 'VENCIDA') return true;
    if (s === 'PENDING' || s === 'PENDENTE') {
      return new Date(m.dataVencimento) < new Date();
    }
    return false;
  }).length;

  const mensalidadesPagas = mensalidades.filter(m => {
    const s = (m.status || '').toUpperCase();
    return s === 'RECEIVED' || s === 'CONFIRMED' || s === 'PAGA' || s === 'PAGO';
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!empresa) {
    return (
      <div className="text-center py-20">
        <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Empresa não encontrada.</p>
        <button onClick={() => navigate('/clientes')} className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium">
          Voltar para lista
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/clientes')}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{empresa.nomeFantasia}</h1>
          <p className="text-sm text-gray-500 font-mono">{formatCNPJ(empresa.cnpj)}</p>
        </div>
        <button
          onClick={handleAlterarStatus}
          disabled={alterandoStatus}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            empresa.status
              ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
              : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
          }`}
        >
          {alterandoStatus ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : empresa.status ? (
            <ToggleRight className="h-4 w-4" />
          ) : (
            <ToggleLeft className="h-4 w-4" />
          )}
          {empresa.status ? 'Inativar acesso' : 'Ativar acesso'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-100">
              <GraduationCap className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{contagens.alunosAtivos}</p>
              <p className="text-xs text-gray-500">Alunos ativos ({contagens.alunos} total)</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-secondary-100">
              <Users className="h-5 w-5 text-secondary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{contagens.usuarios}</p>
              <p className="text-xs text-gray-500">Usuários</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{mensalidadesPagas}</p>
              <p className="text-xs text-gray-500">Mensalidades pagas</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${mensalidadesVencidas > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
              <AlertTriangle className={`h-5 w-5 ${mensalidadesVencidas > 0 ? 'text-red-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${mensalidadesVencidas > 0 ? 'text-red-600' : 'text-gray-900'}`}>{mensalidadesVencidas}</p>
              <p className="text-xs text-gray-500">Mensalidades vencidas</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Dados da empresa</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Building2 className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-500">Razão Social</p>
                <p className="text-sm font-medium text-gray-900">{empresa.razaoSocial || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{empresa.email || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-500">Telefone</p>
                <p className="text-sm font-medium text-gray-900">{empresa.telefone || '-'}</p>
              </div>
            </div>
            {empresa.logradouro && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-500">Endereço</p>
                  <p className="text-sm font-medium text-gray-900">
                    {empresa.logradouro}{empresa.numero ? `, ${empresa.numero}` : ''}{empresa.complemento ? ` - ${empresa.complemento}` : ''}
                    {empresa.bairro ? `, ${empresa.bairro}` : ''}{empresa.cidade ? ` - ${empresa.cidade}` : ''}
                    {empresa.cep ? ` | CEP: ${empresa.cep}` : ''}
                  </p>
                </div>
              </div>
            )}
            {empresa.site && (
              <div className="flex items-start gap-3">
                <Globe className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-500">Site</p>
                  <p className="text-sm font-medium text-primary-600">{empresa.site}</p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-500">Cadastro</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(empresa.createDate)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Plano contratado</h2>
          {empresa.plano ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-primary-50 border border-primary-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-primary-800">{empresa.plano.nome}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    empresa.plano.status === 'ativo'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {empresa.plano.status === 'ativo' ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-primary-900">{formatCurrency(empresa.plano.valor)}<span className="text-sm font-normal text-primary-600">/mês</span></p>
                <p className="text-xs text-primary-600 mt-2">Contratado em {formatDate(empresa.plano.dataContratacao)}</p>
              </div>

              {empresa.asaasConfig && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Asaas</p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Customer ID: <span className="font-mono text-gray-700">{empresa.asaasConfig.customerId || '-'}</span></p>
                    {empresa.asaasConfig.subscriptionId && (
                      <p>Subscription: <span className="font-mono text-gray-700">{empresa.asaasConfig.subscriptionId}</span></p>
                    )}
                    <p>Ambiente: <span className="font-mono text-gray-700">{empresa.asaasConfig.environment || '-'}</span></p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Nenhum plano contratado.</p>
            </div>
          )}
        </div>
      </div>

      {assinaturas.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Assinaturas Asaas</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">ID</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Descrição</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Valor</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Ciclo</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Próx. Vencimento</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {assinaturas.map((ass) => {
                  const statusInfo = getStatusAssinatura(ass.status);
                  return (
                    <tr key={ass.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-xs font-mono text-gray-600">{ass.id}</td>
                      <td className="px-3 py-2.5 text-sm text-gray-900">{ass.description || '-'}</td>
                      <td className="px-3 py-2.5 text-sm text-gray-900 text-center">{formatCurrency(ass.value)}</td>
                      <td className="px-3 py-2.5 text-sm text-gray-600 text-center">{ass.cycle}</td>
                      <td className="px-3 py-2.5 text-sm text-gray-600 text-center">{formatDate(ass.nextDueDate)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.icon} {statusInfo.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Histórico de Mensalidades</h2>
          <span className="text-sm text-gray-500">{mensalidades.length} registro(s)</span>
        </div>
        {mensalidades.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Referência</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Descrição</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Valor</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Vencimento</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Pagamento</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {mensalidades
                  .sort((a, b) => {
                    const dateA = a.dataVencimento || a.dueDate || '';
                    const dateB = b.dataVencimento || b.dueDate || '';
                    return new Date(dateB).getTime() - new Date(dateA).getTime();
                  })
                  .map((m, idx) => {
                    const rawStatus = (m.status || '').toUpperCase();
                    const isPaga = rawStatus === 'RECEIVED' || rawStatus === 'CONFIRMED' || rawStatus === 'PAGA' || rawStatus === 'PAGO';
                    const isVencida = !isPaga && (rawStatus === 'OVERDUE' || rawStatus === 'VENCIDA' || ((rawStatus === 'PENDING' || rawStatus === 'PENDENTE') && new Date(m.dataVencimento) < new Date()));
                    const isPendente = !isPaga && !isVencida;

                    let statusLabel = m.status || '-';
                    let statusColor = 'bg-gray-100 text-gray-600';
                    let statusIcon = <FileText className="h-3.5 w-3.5" />;

                    if (isPaga) {
                      statusLabel = 'Paga';
                      statusColor = 'bg-green-100 text-green-700';
                      statusIcon = <CheckCircle className="h-3.5 w-3.5" />;
                    } else if (isVencida) {
                      statusLabel = 'Vencida';
                      statusColor = 'bg-red-100 text-red-700';
                      statusIcon = <AlertTriangle className="h-3.5 w-3.5" />;
                    } else if (isPendente) {
                      statusLabel = 'Pendente';
                      statusColor = 'bg-yellow-100 text-yellow-700';
                      statusIcon = <Clock className="h-3.5 w-3.5" />;
                    }

                    const valor = m.valor ?? m.value ?? 0;
                    const vencimento = m.dataVencimento || '';
                    const pagamento = m.dataPagamento || '';
                    const descricao = m.descricao || m.description || m.codigo || '-';
                    const referencia = m.asaasPaymentId || m.codigo || m.id || '-';

                    return (
                      <tr key={m.id || idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 text-xs font-mono text-gray-600">{referencia}</td>
                        <td className="px-3 py-2.5 text-sm text-gray-900">{descricao}</td>
                        <td className="px-3 py-2.5 text-sm text-gray-900 text-center">{typeof valor === 'number' && valor > 0 ? formatCurrency(valor) : '-'}</td>
                        <td className="px-3 py-2.5 text-sm text-gray-600 text-center">{formatDate(vencimento)}</td>
                        <td className="px-3 py-2.5 text-sm text-gray-600 text-center">{formatDate(pagamento)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                            {statusIcon} {statusLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Nenhuma mensalidade encontrada.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClienteDetalhes;

